import { useState, useEffect, useCallback, useMemo } from "react";
import { LOGIN_ALLOWED_LEAGUES } from "@/constants/login-allowed-leagues";
import { decodeJWT, isTokenExpired } from "@/utils/jwt-decoder";
import { validateRouteAccessFromCookie } from "@/utils/auth-middleware";

interface AuthState {
  token: string | null;
  isAuthenticated: boolean;
  isInternalUser: boolean;
  companyId: number | null;
  userEmail: string | null;
  userId: number | null;
  userSpaces: number[];
  isLoading: boolean;
}

export const useAuthGuard = () => {
  const [authState, setAuthState] = useState<AuthState>({
    token: null,
    isAuthenticated: false,
    isInternalUser: false,
    companyId: null,
    userEmail: null,
    userId: null,
    userSpaces: [],
    isLoading: true,
  });

  const extractToken = useCallback((): string | null => {
    try {
      const cookies = document.cookie.split(";");
      const uTokenCookie = cookies.find((cookie) =>
        cookie.trim().startsWith("u_token="),
      );

      if (uTokenCookie) {
        return uTokenCookie.split("=")[1];
      }

      return null;
    } catch (error) {
      console.error("Erro ao extrair token do cookie:", error);
      return null;
    }
  }, []);

  const isInternalUser = useCallback((companyId: number): boolean => {
    return LOGIN_ALLOWED_LEAGUES.includes(companyId) || !companyId;
  }, []);

  const canAccessRoute = useCallback(
    (route: string): boolean => {
      try {
        // Determine if this is an external route
        const isExternalRoute = route.includes("/external/");

        // Use our new validation logic
        const authResult = validateRouteAccessFromCookie(isExternalRoute);

        return authResult.hasAccess;
      } catch (error) {
        console.error("Error validating route access:", error);
        return false;
      }
    },
    [], // No dependencies needed as validateRouteAccessFromCookie handles everything
  );

  useEffect(() => {
    const updateAuthState = () => {
      const token = extractToken();

      if (!token) {
        setAuthState((prev) => ({
          ...prev,
          token: null,
          isAuthenticated: false,
          isInternalUser: false,
          companyId: null,
          userEmail: null,
          userId: null,
          userSpaces: [],
          isLoading: false,
        }));
        return;
      }

      if (isTokenExpired(token)) {
        console.warn("Token expirado");
        setAuthState((prev) => ({
          ...prev,
          token: null,
          isAuthenticated: false,
          isInternalUser: false,
          companyId: null,
          userEmail: null,
          userId: null,
          userSpaces: [],
          isLoading: false,
        }));
        return;
      }

      const payload = decodeJWT(token);
      if (!payload) {
        setAuthState((prev) => ({
          ...prev,
          token: null,
          isAuthenticated: false,
          isInternalUser: false,
          companyId: null,
          userEmail: null,
          userId: null,
          userSpaces: [],
          isLoading: false,
        }));
        return;
      }

      const companyId = payload.companyId;
      const isInternal = isInternalUser(companyId);
      const userSpaces = payload.spaces || [];

      setAuthState((prev) => ({
        ...prev,
        token,
        isAuthenticated: true,
        isInternalUser: isInternal,
        companyId,
        userEmail: payload.email,
        userId: payload.sub,
        userSpaces,
        isLoading: false,
      }));

      console.log("🔐 Estado de autenticação atualizado:", {
        isInternalUser: isInternal,
        companyId,
        userEmail: payload.email,
        userSpaces,
      });
    };

    updateAuthState();
  }, [extractToken, isInternalUser]);

  const memoizedValues = useMemo(
    () => ({
      isExternalUser: !authState.isInternalUser,
    }),
    [authState.isInternalUser],
  );

  return {
    ...authState,
    ...memoizedValues,
    canAccessRoute,
    extractToken,
  };
};
